import * as Dialog from '@radix-ui/react-dialog';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import s from './ChangePasswordDialog.module.scss';

const FormSchema = z
  .object({
    current_password: z.string().min(1, 'Required'),
    new_password: z.string().min(8, 'At least 8 characters'),
    confirm_password: z.string().min(1, 'Required'),
  })
  .refine((d) => d.new_password === d.confirm_password, {
    path: ['confirm_password'],
    message: 'Passwords do not match',
  });
type FormValues = z.infer<typeof FormSchema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ChangePasswordDialog({ open, onOpenChange }: Props) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { current_password: '', new_password: '', confirm_password: '' },
  });

  function close(next: boolean): void {
    onOpenChange(next);
    if (!next) {
      reset();
      setSubmitError(null);
      setDone(false);
    }
  }

  const onSubmit = async (values: FormValues): Promise<void> => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          current_password: values.current_password,
          new_password: values.new_password,
        }),
      });
      if (res.status === 204) {
        setDone(true);
        return;
      }
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (body.error === 'invalid_current_password') {
        setError('current_password', { message: 'That’s not your current password' });
      } else if (body.error === 'same_password') {
        setError('new_password', { message: 'New password must be different' });
      } else {
        setSubmitError(`Could not change password (${res.status}).`);
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={close}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        <Dialog.Content className={s.content}>
          <Dialog.Title className={s.title}>Change password</Dialog.Title>

          {done ? (
            <div>
              <p className={s.success} role="status">
                Password changed. Your other sessions have been signed out.
              </p>
              <div className={s.actions}>
                <button type="button" className={s.submit} onClick={() => close(false)}>
                  Done
                </button>
              </div>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                void handleSubmit(onSubmit)(e);
              }}
              noValidate
            >
              {submitError && (
                <div className={s.formError} role="alert">
                  {submitError}
                </div>
              )}

              <div className={s.field}>
                <label className={s.label} htmlFor="cp-current">
                  Current password
                </label>
                <input
                  id="cp-current"
                  type="password"
                  autoComplete="current-password"
                  className={s.input}
                  aria-invalid={!!errors.current_password}
                  {...register('current_password')}
                />
                {errors.current_password && (
                  <div className={s.fieldError}>{errors.current_password.message}</div>
                )}
              </div>

              <div className={s.field}>
                <label className={s.label} htmlFor="cp-new">
                  New password
                </label>
                <input
                  id="cp-new"
                  type="password"
                  autoComplete="new-password"
                  className={s.input}
                  aria-invalid={!!errors.new_password}
                  {...register('new_password')}
                />
                {errors.new_password && (
                  <div className={s.fieldError}>{errors.new_password.message}</div>
                )}
              </div>

              <div className={s.field}>
                <label className={s.label} htmlFor="cp-confirm">
                  Confirm new password
                </label>
                <input
                  id="cp-confirm"
                  type="password"
                  autoComplete="new-password"
                  className={s.input}
                  aria-invalid={!!errors.confirm_password}
                  {...register('confirm_password')}
                />
                {errors.confirm_password && (
                  <div className={s.fieldError}>{errors.confirm_password.message}</div>
                )}
              </div>

              <div className={s.actions}>
                <button
                  type="button"
                  className={s.cancel}
                  onClick={() => close(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button type="submit" className={s.submit} disabled={submitting}>
                  {submitting ? 'Saving…' : 'Change password'}
                </button>
              </div>
            </form>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
